import '../styles/mandant.css';
import '../client/account/account.css';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { LegalLinks } from '../components/LegalLinks';
import { LangSwitch, t, tn } from '../i18n';
import {
  AgbField,
  BANK_ORDER,
  BankFields,
  COMPANY_ORDER,
  companyPayload,
  fieldId,
  FirmFields,
  focusFirstError,
  hasErrors,
  serverErrorField,
  TextField,
  useForm,
  validateCompany,
} from './CompanyFields';
import { emailOk } from './company';
import { useSession } from './session';

const ORDER = [...COMPANY_ORDER, 'contactName', 'email', 'phone', ...BANK_ORDER, 'password', 'unternehmer', 'agb', 'av'];

// Registrierung eines neuen Mandanten. Danach ist der Mandant angemeldet und sieht den Wartebildschirm.
export function RegisterPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const f = useForm({});
  const [error, setError] = useState<string | null>(null);
  // E-Mail schon vergeben: Meldung am E-Mail-Feld mit Link zur Anmeldung
  const [taken, setTaken] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = `${t('Registrieren')} · Elbcons`;
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setTaken(false);
    const v = f.values;
    const errors = validateCompany(v);
    if (!(v.contactName ?? '').trim()) errors.contactName = t('Bitte geben Sie Vor- und Nachnamen ein.');
    if (!(v.email ?? '').trim()) errors.email = t('Bitte geben Sie Ihre E-Mail ein.');
    else if (!emailOk(v.email ?? '')) errors.email = t('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
    if ((v.password ?? '').length < 8) errors.password = t('Das Passwort muss mindestens 8 Zeichen haben.');
    if (hasErrors(errors)) {
      f.setErrors(errors);
      focusFirstError(ORDER, errors);
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/register', {
        ...companyPayload(v),
        contactName: (v.contactName ?? '').trim(),
        email: (v.email ?? '').trim(),
        phone: (v.phone ?? '').trim(),
        password: v.password ?? '',
      });
      await refresh();
      navigate('/app', { replace: true });
    } catch (err) {
      const text = errorText(err);
      // Meldung am passenden Feld (dort, wo der Fokus hinspringt), sonst über dem Knopf.
      // E-Mail schon vergeben: am E-Mail-Feld mit Link zur Anmeldung.
      const emailTaken = err instanceof ApiError && err.code === 'email_taken';
      const field = emailTaken ? 'email' : serverErrorField(text);
      if (field) {
        setTaken(emailTaken);
        f.setErrors({ [field]: text });
        document.getElementById(fieldId(field))?.focus();
      } else setError(text);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mapp">
      <div className="regwrap">
        <div className="in">
          <div className="brandrow">
            <span className="logo wm" role="img" aria-label="Elbcons" />
            {/* .brandrow hat keinen eigenen Abstand für den Umschalter: nach rechts schieben */}
            <span style={{ marginInlineStart: 'auto' }}>
              <LangSwitch />
            </span>
          </div>
          <h1>{t('Registrieren')}</h1>
          <p className="lead">
            {t('Legen Sie Ihren Zugang an. Ihre Kanzlei prüft die Angaben und schaltet Ihren Account frei.')}
          </p>
          <p className="lead b2b">
            {t('Nur für Unternehmen (Gewerbe, Freiberufler, Gesellschaften). Kein Angebot für Privatpersonen.')}
          </p>
          <form className="form" onSubmit={submit} noValidate>
            <FirmFields f={f} />
            <h3>{t('Ansprechpartner')}</h3>
            <TextField f={f} name="contactName" label={t('Vor- und Nachname')} required maxLength={160} autoComplete="name" />
            <div className="two">
              <TextField
                f={f}
                name="email"
                label={t('E-Mail')}
                type="email"
                required
                maxLength={160}
                autoComplete="email"
                errorExtra={
                  taken && (
                    <>
                      {' '}
                      <Link to="/login">{t('Zur Anmeldung')}</Link>
                    </>
                  )
                }
              />
              <TextField f={f} name="phone" label={t('Telefon')} type="tel" maxLength={160} autoComplete="tel" />
            </div>
            <BankFields f={f} />
            <h3>{t('Zugang')}</h3>
            <TextField
              f={f}
              name="password"
              label={t('Passwort (mindestens 8 Zeichen)')}
              type="password"
              required
              minLength={8}
              maxLength={200}
              autoComplete="new-password"
            />
            <AgbField f={f} />
            {error && (
              <p className="formerr" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn2 dark" disabled={busy}>
              {busy ? t('Einen Moment …') : t('Registrieren')}
            </button>
          </form>
          <p className="regfoot">{tn('Schon registriert? {link}', { link: <Link to="/login">{t('Anmelden')}</Link> })}</p>
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}
